import { HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { LoaderSpinnerTypes } from '@carm/_core/models/loader-types.model';
import * as dayjs from 'dayjs';
import { Observable} from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DatePipe } from '@angular/common';

import {
  IGetDeclarations,
  IGetDutyDrawbacks,
  IGetMassAdjustments,
  IGetMasterProResponse,
  IGetReleasesResponse,
  IGetROIs,
  IGetTemporaryImports,
  ISubmittedDeclaration,
  ISubmittedSDAsResponse,
} from '../models';

@Injectable()
export class CadListService extends BaseApiService {
  constructor(protected injector: Injector,  private datePipe: DatePipe) {
    super(injector);
  }

  get baseCadListUrlBP(): string {
    return `${this.endPointBP}/cad/list`;
  }

  // Returns the list of submitted declarations
  public getDeclarationsList(
    searchString: string,
    minDate: Date,
    maxDate: Date,
    sortField: string,
    currentPage: number,
    pageSize: number
  ): Observable<IGetDeclarations> {
    const endPoint = `${this.baseCadListUrlBP}/declarations`;
    const params = this.getHttpPameter(
      searchString,
      minDate,
      maxDate,
      sortField,
      currentPage,
      pageSize
    );
    return this.httpAuthGet<IGetDeclarations>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  // Returns Perticular version of the declaration using transaction No
  public getDeclaratonByVersion(
    transactionNo: string,
    versionNo: string
  ): Observable<ISubmittedDeclaration> {

    let searchString = ` substringof(TransactionNo, '${transactionNo}') and substringof(Version, '${versionNo}')`;
    const endPoint = `${this.baseCadListUrlBP}/declarations`;
    const params = this.getHttpPameter(
      searchString,
      new Date(1970, 0, 1),
      new Date(),
      'ACCOUNTING_DATE_DESC',
      1,
      1
    );
    return this.httpAuthGet<IGetDeclarations>(endPoint, params)
    .pipe(map(resp=> {
       let submittedDeclarationList = resp.declarations;
       return submittedDeclarationList && submittedDeclarationList.length > 0
       ? submittedDeclarationList[0] : null
    }));
  }

  // Returns the list of Mass Adjustments
  public getMassAdjustmentsList(
    minDate: Date,
    maxDate: Date,
    pageSize: number = 500,
    searchString?: string
  ): Observable<IGetMassAdjustments> {
    const endPoint = `${this.baseCadListUrlBP}/mass-adjustments`;
    const params = this.getHttpPameter(
      searchString,
      minDate,
      maxDate,
      '',
      1,
      pageSize
    );
    return this.httpAuthGet<IGetMassAdjustments>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  // Returns the list of submitted declarations with errors
  public getDeclarationErrorsList(
    searchString: string,
    minDate: Date,
    maxDate: Date,
    sortField: string,
    currentPage: number,
    pageSize: number
  ): Observable<IGetDeclarations> {
    const endPoint = `${this.baseCadListUrlBP}/declarations`;
    const params = this.getHttpPameter(
      searchString,
      minDate,
      maxDate,
      sortField,
      currentPage,
      pageSize
    );
    return this.httpAuthGet<IGetDeclarations>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  // Returns the list of Mass Adjustments drafts
  public getMassAdjustmentsDraftList(
    minDate: Date,
    maxDate: Date
  ): Observable<IGetMassAdjustments> {
    const endPoint = `${this.baseCadListUrlBP}/mass-adjustment-drafts`;
    const params = this.getHttpPameter('', minDate, maxDate, '', 1, 500);
    return this.httpAuthGet<IGetMassAdjustments>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  // Returns the list of temporary imports
  public getTemporaryImportsList(
    searchString: string,
    // minDate: Date,
    // maxDate: Date,
    sortField: string,
    currentPage: number,
    pageSize: number
  ): Observable<IGetTemporaryImports> {
    const endPoint = `${this.baseCadListUrlBP}/temporary-importations`;
    const params = new HttpParams()
      .set('currentPage', currentPage.toString())
      .set('pageSize', pageSize.toString())
      .set('temporaryImportsSort', sortField)
      .set('searchString', searchString || null);
    return this.httpAuthGet<IGetTemporaryImports>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  // Returns the list of duty drawbacks
  public getDutyDrawbacksList(
    searchString: string,
    sort: string,
    currentPage: number,
    pageSize: number
  ): Observable<IGetDutyDrawbacks> {
    const endPoint = `${this.baseCadListUrlBP}/duty-drawbacks`;
    const params = new HttpParams()
      .set('searchString', searchString || null)
      .set('pageSize', pageSize.toString())
      .set('currentPage', currentPage.toString())
      .set('dutyDrawbacksSummarySort', sort);
    return this.httpAuthGet<IGetDutyDrawbacks>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  // Returns the list of ROIs
  public getROIList(
    searchString: string,
    startDate: Date,
    endDate: Date,
    sortField: string,
    currentPage: number,
    pageSize: number
  ): Observable<IGetROIs> {
    const endPoint = `${this.baseCadListUrlBP}/rois`;
    const params = new HttpParams()
      .set('currentPage', currentPage.toString())
      .set('pageSize', pageSize.toString())
      .set(
        'startDate',
        this.datePipe.transform(startDate, 'yyyy-MM-dd')
      )
      .set(
        'endDate',
        this.datePipe.transform(endDate, 'yyyy-MM-dd')
      )
      .set('roiSort', sortField)
      .set('searchString', searchString || null);
    return this.httpAuthGet<IGetROIs>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  public getReleases(
    minDate: Date,
    maxDate: Date,
    sortField: string,
    currentPage: number,
    pageSize: number,
    searchString: string = ''
  ): Observable<IGetReleasesResponse> {
    const endPoint = `${this.baseCadListUrlBP}/releases`;
    const params = this.getHttpPameter(
      searchString,
      minDate,
      maxDate,
      sortField,
      currentPage,
      pageSize
    );
    return this.httpAuthGet<IGetReleasesResponse>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  public getReleasesByBusinessAndProgram(
    bn9: string,
    programId: string,
    minDate: Date,
    maxDate: Date,
    sortField: string,
    currentPage: number,
    pageSize: number,
    searchString: string = ''
  ): Observable<IGetReleasesResponse> {
    const endPoint = `${environment.baseApiUrls}/business/${bn9}/program/${programId}/cad/list/releases`;
    const params = this.getHttpPameter(
      searchString,
      minDate,
      maxDate,
      sortField,
      currentPage,
      pageSize
    );
    return this.httpAuthGet<IGetReleasesResponse>(endPoint, params);
  }

  private getHttpPameter(
    searchString: string,
    startDate: Date,
    endDate: Date,
    sortField: string,
    currentPage: number,
    pageSize: number
  ): HttpParams {

    if (!startDate){
      startDate = new Date(1970, 0, 1);
    }

    if (!endDate){
      endDate = new Date();
    }


    const params = new HttpParams()
      .set('currentPage', currentPage.toString())
      .set('pageSize', pageSize.toString())
      .set(
        'startDate',
        this.datePipe.transform(startDate, 'yyyy-MM-dd')
      ) // default to today - 100years
      .set(
        'endDate',
        this.datePipe.transform(endDate, 'yyyy-MM-dd')
      ) // default to today + 1 day
      .set('declarationsSort', sortField)
      .set('searchString', searchString || null);
    return params;
  }

  getMasterProvisionalAuthorizationRequests(
    minDate: Date,
    maxDate: Date,
    currentPage: number,
    pageSize: number,
    sortField: string,
    searchString: string = ''
  ): Observable<IGetMasterProResponse> {
    const endPoint = `${this.baseCadListUrlBP}/authorization-requests`;
    const params = new HttpParams()
      .set('startDate', dayjs(minDate).format('YYYY-MM-DD'))
      .set('endDate', dayjs(maxDate).format('YYYY-MM-DD'))
      .set('currentPage', currentPage.toString())
      .set('pageSize', pageSize.toString())
      .set('mpReqSort', sortField)
      .set('searchString', searchString);
    return this.httpAuthGet<IGetMasterProResponse>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  getListSubmittedSDAs(
    minDate: Date,
    maxDate: Date,
    currentPage: number,
    pageSize: number,
    sortField: string,
    searchString: string = ''
  ): Observable<ISubmittedSDAsResponse> {
    const endPoint = `${this.baseCadListUrlBP}/summary-drawback-activities`;
    const params = new HttpParams()
      .set('startDate', dayjs(minDate).format('YYYY-MM-DD'))
      .set('endDate', dayjs(maxDate).format('YYYY-MM-DD'))
      .set('currentPage', currentPage.toString())
      .set('pageSize', pageSize.toString())
      .set('sdaSort', sortField)
      .set('searchString', searchString);
    return this.httpAuthGet<ISubmittedSDAsResponse>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }
}
